import { createContext, useContext, useMemo, useState, useEffect, useCallback } from "react";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";
import { ptBR } from '@mui/material/locale';
import CssBaseline from "@mui/material/CssBaseline";

const ThemeContext = createContext();

export const useThemeMode = () => useContext(ThemeContext);

// Temas disponíveis
export const THEME_MODES = {
  DEFAULT: 'default',
  DARK: 'dark',
  CITRICO: 'citrico'
};

// Cor padrão da aplicação
const PRIMARY_COLOR = '#20ac9c';

// Cores específicas para cada tema
const THEME_COLORS = {
  [THEME_MODES.DEFAULT]: {
    primary: {
      main: PRIMARY_COLOR,
      light: '#4dbeb3',
      dark: '#167a6f',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    text: {
      primary: '#212121',
      secondary: '#757575',
    },
  },
  [THEME_MODES.DARK]: {
    primary: {
      main: '#4dbeb3',
      light: '#7dd3c8',
      dark: '#167a6f',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ffb74d',
      light: '#ffcc80',
      dark: '#f57c00',
      contrastText: '#ffffff',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
  },
  [THEME_MODES.CITRICO]: {
    primary: {
      main: '#FF9800', // Orange 500
      light: '#FFB74D', // Orange 200
      dark: '#F57C00', // Orange 700
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#FF5722', // Deep Orange 500
      light: '#FF8A65',
      dark: '#E64A19',
      contrastText: '#ffffff',
    },
    background: {
      default: '#FFF3E0', // Orange 50
      paper: '#ffffff',
    },
    text: {
      primary: '#E65100', // Orange 900 for text to fit theme? Or standard dark? Let's use standard dark for readability but maybe warm grey.
      secondary: '#EF6C00', // Orange 800
    },
  }
};

export function ThemeProvider({ children }) {
  // Carregar tema salvo no localStorage ou usar padrão
  const [themeMode, setThemeMode] = useState(() => {
    const savedTheme = localStorage.getItem('themeMode');
    if (savedTheme && THEME_COLORS[savedTheme]) {
      return savedTheme;
    }
    return THEME_MODES.DEFAULT;
  });

  // Carregar tema das preferências do usuário quando disponível
  useEffect(() => {
    const loadThemeFromPreferences = () => {
      try {
        const prefs = localStorage.getItem('user_preferences');
        if (prefs) {
          const preferences = JSON.parse(prefs);
          if (preferences.theme_mode && THEME_COLORS[preferences.theme_mode]) {
            setThemeMode(preferences.theme_mode);
            localStorage.setItem('themeMode', preferences.theme_mode);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar tema das preferências:', error);
      }
    };

    // Carregar imediatamente
    loadThemeFromPreferences();

    // Escutar mudanças no localStorage
    const handleStorageChange = (e) => {
      if (e.key === 'user_preferences' || e.key === 'themeMode') {
        loadThemeFromPreferences();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Escutar mudanças customizadas (quando o mesmo tab atualiza localStorage)
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function (key, value) {
      originalSetItem.apply(this, arguments);
      if (key === 'user_preferences' || key === 'themeMode') {
        setTimeout(() => loadThemeFromPreferences(), 100);
      }
    };

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      localStorage.setItem = originalSetItem;
    };
  }, []);

  // Salvar tema no localStorage sempre que mudar
  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);
  }, [themeMode]);

  // Função para mudar tema
  const changeTheme = useCallback((newTheme) => {
    if (THEME_COLORS[newTheme] && newTheme !== themeMode) {
      setThemeMode(newTheme);
      localStorage.setItem('themeMode', newTheme);
    } else if (!THEME_COLORS[newTheme]) {
      console.warn(`Tema inválido: ${newTheme}, usando tema padrão`);
      setThemeMode(THEME_MODES.DEFAULT);
    }
  }, [themeMode]);

  // Função para alternar entre temas
  const toggleTheme = () => {
    const themes = Object.values(THEME_MODES);
    const currentIndex = themes.indexOf(themeMode);
    const nextIndex = (currentIndex + 1) % themes.length;
    setThemeMode(themes[nextIndex]);
  };

  const theme = useMemo(
    () => {
      const colors = THEME_COLORS[themeMode] || THEME_COLORS[THEME_MODES.DEFAULT];

      return createTheme({
        palette: {
          mode: themeMode === THEME_MODES.DARK ? 'dark' : 'light',
          ...colors,
        },
        typography: {
          fontFamily: [
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
          ].join(','),
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                borderRadius: 8,
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 12,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              },
            },
          },
        },
      });
    },
    [themeMode]
  );

  // Aplicar localização pt-BR ao tema
  const themeWithLocale = useMemo(
    () => createTheme(theme, ptBR),
    [theme]
  );

  return (
    <ThemeContext.Provider value={{
      mode: themeMode,
      changeTheme,
      toggleTheme,
      availableThemes: THEME_MODES,
      currentTheme: themeMode
    }}>
      <MuiThemeProvider theme={themeWithLocale}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export default ThemeProvider;

